import { DEFAULT_ERROR_MESSAGE } from "@constants";
import { TFunction } from "i18next";
import { APIErrorCodes, ErrorResponse, QueryError } from "kz-ui-sdk";

/**
 * Turtle mode: backend returns `ForbiddenError` with `try again later`.
 */
export const isTurtleModeForbiddenError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const e = error as {
    data?: { error?: { message?: string } };
  };
  return e.data?.error?.message === APIErrorCodes.TryAgainLater;
};

type ApiErrorDataShape = { error?: { message?: string } };

export const getApiErrorMessage = (error: unknown): string | undefined => {
  if (!error || typeof error !== "object") return undefined;

  if ("data" in error) {
    const data = (error as { data?: unknown }).data;
    if (data && typeof data === "object" && "error" in data) {
      const err = (data as ApiErrorDataShape).error;
      return err?.message;
    }
  }

  return undefined;
};

/** i18n key for registration lockdown copy when API returns TryAgainLater. */
export const REGISTRATION_UNAVAILABLE_TRY_AGAIN_LATER_I18N_KEY =
  "Sorry registrations are unavailable. Please try again later.";

/** i18n key for deposit lockdown copy when API returns TryAgainLater. */
export const DEPOSIT_UNAVAILABLE_TRY_AGAIN_LATER_I18N_KEY = "Sorry deposits are unavailable. Please try again later.";

export const resolveAuthResponseError = (error: QueryError<ErrorResponse>, t: TFunction) => {
  if (error?.data?.error?.message === "request rate limited.") {
    const retryAfter = +error?.data?.error?.errors?.["rateLimit"] - +error?.data?.error?.errors?.["diff"];
    if (retryAfter !== null) {
      const minutes = Math.floor(retryAfter / 60);
      const seconds = retryAfter % 60;
      return t(`please wait {{minutes}} and {{seconds}} before trying again`, {
        minutes,
        seconds,
      });
    }
    return t(DEFAULT_ERROR_MESSAGE);
  }

  // Map register error message
  switch (error?.data?.error?.message) {
    case APIErrorCodes.Auth.AccountSuspended:
    case APIErrorCodes.Auth.AccountClosed:
    case APIErrorCodes.Auth.AccountFrozen:
      return t("Request failed, please contact customer service.");
    case APIErrorCodes.Auth.CredentialsTempLocked:
    case APIErrorCodes.Auth.CredentialsTempBlocked: {
      const remainingTime = error?.data?.error?.errors?.remaining as unknown as number;
      const minutes = Math.floor(remainingTime / 60);
      const seconds = remainingTime % 60;

      return t("try again in {{minutes}} and {{seconds}}.", { minutes, seconds });
    }
    case APIErrorCodes.TryAgainLater:
      return t(REGISTRATION_UNAVAILABLE_TRY_AGAIN_LATER_I18N_KEY);
    default:
      return t(error?.data?.error?.message ?? DEFAULT_ERROR_MESSAGE);
  }
};
